import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

interface Feature {
  icon: string;
  title: string;
  description: string;
}

interface ProcessStep {
  step: number;
  title: string;
  description: string;
}

interface CaseStudy {
  title: string;
  client: string;
  industry: string;
  challenge: string;
  solution: string;
  results: {
    metric: string;
    value: string;
  }[];
  image: string;
}

interface Testimonial {
  name: string;
  role: string;
  company: string;
  content: string;
  avatar: string;
}

@Component({
  selector: 'app-bulk-whatsapp',
  imports: [CommonModule, RouterLink],
  templateUrl: './bulk-whatsapp.html',
  styleUrl: './bulk-whatsapp.css',
  standalone: true
})
export class BulkWhatsapp {
  features: Feature[] = [
    {
      icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
      title: 'Rich Media Messages',
      description: 'Send images, videos, documents, and location pins. Create engaging multimedia experiences that capture attention and drive action on the world\'s most popular messaging platform.'
    },
    {
      icon: 'M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z',
      title: 'Interactive Templates',
      description: 'Pre-approved message templates with quick reply buttons and call-to-action features. Streamline customer interactions with structured, compliant messaging.'
    },
    {
      icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
      title: 'Broadcast Lists',
      description: 'Send personalized messages to thousands of contacts simultaneously. Maintain one-on-one conversation feel while reaching your entire audience at scale.'
    },
    {
      icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
      title: 'Delivery Tracking',
      description: 'Real-time tracking of message delivery, read receipts, and engagement metrics. Know exactly when your messages are delivered and opened by recipients.'
    },
    {
      icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
      title: 'Smart Scheduling',
      description: 'Schedule messages based on recipient timezone and optimal engagement times. Automate campaigns while maintaining perfect timing for maximum impact.'
    },
    {
      icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
      title: 'Compliance Built-in',
      description: 'Automatic opt-in/opt-out management, template approval workflows, and GDPR compliance. Stay within WhatsApp guidelines with our built-in safety features.'
    }
  ];

  process: ProcessStep[] = [
    {
      step: 1,
      title: 'Get Verified',
      description: 'Register your business with WhatsApp Business API. We handle the verification process and setup, getting you approved quickly.'
    },
    {
      step: 2,
      title: 'Create Templates',
      description: 'Design message templates with text, images, and buttons. Submit for WhatsApp approval through our streamlined workflow.'
    },
    {
      step: 3,
      title: 'Build Contact Lists',
      description: 'Import contacts who have opted in to receive WhatsApp messages. Segment audiences for targeted, relevant communication.'
    },
    {
      step: 4,
      title: 'Launch Campaigns',
      description: 'Send broadcast messages, track engagement, and measure results. Optimize based on real-time analytics and insights.'
    }
  ];

  caseStudy: CaseStudy = {
    title: 'Customer Support & Order Updates',
    client: 'FoodDelivery Express',
    industry: 'Food Delivery & Quick Commerce',
    challenge: 'FoodDelivery needed to provide real-time order updates and handle customer support queries efficiently. High SMS costs and low engagement rates were impacting customer satisfaction and operational efficiency.',
    solution: 'Implemented HoneyKode\'s Bulk WhatsApp platform for automated order notifications, delivery updates, and customer support. Integrated with existing order management system for real-time status updates and enabled two-way conversations for support.',
    results: [
      { metric: 'Message Read Rate', value: '97%' },
      { metric: 'Customer Response Time', value: '78% faster' },
      { metric: 'Support Ticket Volume', value: '45% reduction' },
      { metric: 'Customer Satisfaction', value: '4.8/5.0' },
      { metric: 'Monthly Messages', value: '2.3M+' },
      { metric: 'Cost Savings', value: '58% vs SMS' }
    ],
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=500&fit=crop'
  };

  testimonial: Testimonial = {
    name: 'Rajesh Kumar',
    role: 'Head of Customer Experience',
    company: 'FoodDelivery Express',
    content: 'WhatsApp has become our primary customer communication channel, and HoneyKode made the implementation seamless. The read rates are incredible compared to SMS or email. Customers love receiving order updates on WhatsApp, and our support team can handle twice as many queries. The rich media capabilities let us send images of orders, location maps, and quick reply buttons that make everything faster. This has been a game-changer for our customer experience.',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop'
  };
}
